import { useUserEventLogger } from '@repo/tracking-service';
import cn from 'clsx';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { YesNoBlank } from '@/__generated__/businessLocationsMutation.graphql';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import { CheckList, CheckListGroup, CTA, ProgressBar } from '@/components/survey/common/ui';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { upsertIsFirstLocation } from '@/graphql/mutation/survey/business';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { useUpsertBusinessAnswer } from '@/lib/hooks/api/mutation/survey/use-upsert-business-answer';
import {
  readonlyHometaxAddressIndexAtom,
  readonlyHometaxAddressLocationAtom,
  readonlyIsFirstLocationAtom,
  readonlyLocationsForServerAtom
} from '@/lib/stores/survey/business';

const answers: { value: YesNoBlank; label: string }[] = [
  { value: 'yes', label: '네, 맞아요' },
  { value: 'no', label: '아니요, 이전한 주소예요' }
];

export const IsFirstLocationContainer = () => {
  const router = useRouter();
  const [upsertBusinessAnswer, isLoading] = useUpsertBusinessAnswer(upsertIsFirstLocation);

  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();

  const prevAnswer = useAtomValue(readonlyIsFirstLocationAtom);
  const hometaxAddressLocation = useAtomValue(readonlyHometaxAddressLocationAtom);
  // payload
  const locations = useAtomValue(readonlyLocationsForServerAtom);
  const hometaxAddressIdx = useAtomValue(readonlyHometaxAddressIndexAtom);

  const [selectedAnswer, setSelectedAnswer] = useState<YesNoBlank>('blank');

  useEffect(() => {
    if (prevAnswer !== 'blank' && prevAnswer !== 'addressUnknown') {
      setSelectedAnswer(prevAnswer);
    }
  }, [setSelectedAnswer, prevAnswer]);

  return (
    <>
      <SurveyTopNavigation leftClickEvent={'survey_relocation_back'} />
      <main className={cn(common.main_layout, common.main_cta_space)}>
        <article>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendClickEvent('survey_relocation_confirm', eventAttributes);

              const newLocation = {
                isFirstLocation: selectedAnswer,
                address: hometaxAddressLocation.address,
                year: selectedAnswer === 'yes' ? null : hometaxAddressLocation.year,
                isUnknownRelocationYear: selectedAnswer === 'yes' ? false : hometaxAddressLocation.isUnknownRelocationYear
              };
              const copyLocations = locations.slice();
              copyLocations.splice(hometaxAddressIdx, 1, newLocation);

              upsertBusinessAnswer(
                { isFirstLocation: selectedAnswer, locations: selectedAnswer === 'yes' ? [newLocation] : copyLocations, isCompleted: selectedAnswer === 'yes' },
                {
                  onCompleted: () => {
                    if (selectedAnswer === 'yes') {
                      router.push(SURVEY_PAGES.사업장별질문);
                      return;
                    }
                    router.push(SURVEY_PAGES.주소이전_현재주소_이전연도);
                  }
                }
              );
            }}
          >
            <ProgressBar step={5} />
            <section>
              <h1 className={common.content_title}>
                아래 사업장 주소가
                <br />
                최초로 사업장을 등록한 주소인가요?
              </h1>
              <div className={'py-4'}>
                <p className={'body-13 mb-1 text-gray-700'}>현재(또는 폐업 당시) 사업장 주소</p>
                <h2 className={'headline-16 text-gray-800'}>{hometaxAddressLocation.address}</h2>
              </div>

              <CheckListGroup>
                {answers.map(({ value, label }) => (
                  <CheckList
                    key={value}
                    value={value}
                    checked={selectedAnswer === value}
                    onChange={() => {
                      sendClickEvent(`survey_relocation_${value}`, eventAttributes);

                      setSelectedAnswer(value);
                    }}
                  >
                    {label}
                  </CheckList>
                ))}
              </CheckListGroup>
            </section>

            {selectedAnswer !== 'blank' && (
              <CTA type={'submit'} disabled={isLoading}>
                다음
              </CTA>
            )}
          </form>
        </article>
      </main>
    </>
  );
};
